import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/User';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const USER_API_REDUCER_KEY = 'users';
export const USER_PATH = 'users';

export const userApi = createApi({
    reducerPath: USER_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['User', 'Users'],
    endpoints: (builder) => ({
        getUserList: builder.query<PaginationResult<Entity.User>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ USER_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['Users'],
        }),
        getUser: builder.query<Entity.User, Entity.PrimaryUser>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = userNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ USER_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['User'],
        }),
        getUserMultiple: builder.query<Entity.User, Entity.PrimaryUser[]>({
            query: (ids) => {
                return ({
                    url: `/${ USER_PATH }/ids${Entity.PrimaryUser.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['Users'],
        }),
        getUserPresentation: builder.query<PaginationResult<Entity.User>, Entity.PresentationUserRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ USER_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['Users'],
        }),
        getUserUserEmail: builder.query<PaginationResult<Entity.User>, Entity.FilterUserEmailRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
                return ({
                    url: `/${ USER_PATH }/find/userEmail?${new URLSearchParams(queryParams).toString()}`,
                    method: 'POST',
                    data: request.filter,
                })
            },
            providesTags: ['Users'],
        }),
        
        insertUser: builder.mutation<Entity.User, Entity.CreateUser>({
            query: (toInsert) => {
                return ({
                    url: `/${ USER_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    userApi.util.upsertQueryData('getUser', Entity.PrimaryUser.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['Users'],
        }),
        insertMultipleUser: builder.mutation<Entity.User[], Entity.CreateUser[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ USER_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            userApi.util.upsertQueryData('getUser', Entity.PrimaryUser.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['Users'],
        }),
        updateUser: builder.mutation<Entity.User, Entity.UpdateEntityUser>({
            query: (toUpdate) => {
                return ({
                    url: `/${ USER_PATH }/${toUpdate.id.toPath()}`,
                    method: 'PUT',
                    data: toUpdate.data,
                })
            },
            invalidatesTags: ['Users'],
        }),
        updateMultipleUser: builder.mutation<Entity.User[], Entity.UpdateEntityUser[]>({
            query: (toUpdate) => {
                return ({
                    url: `/${ USER_PATH }/batch`,
                    method: 'PUT',
                    data: toUpdate,
                })
            },
            invalidatesTags: ['Users'],
        }),
        deleteUser: builder.mutation<Entity.User, Entity.User>({
            query: (toDelete) => {
                return ({
                    url: `/${ USER_PATH }/${Entity.PrimaryUser.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['Users'],
        }),
        deleteMultipleUser: builder.mutation<Entity.User[], Entity.PrimaryUser[]>({
            query: (ids) => {
                return ({
                    url: `/${ USER_PATH }/batch${Entity.PrimaryUser.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['Users'],
        }),
    }),
})

export const [userNormalizer, userNormalizerMiddleware] = createNormalizationMiddleware(USER_API_REDUCER_KEY, userApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetUserListQuery,
    useGetUserMultipleQuery,
    useGetUserPresentationQuery,
    useGetUserQuery,
    useInsertUserMutation,
    useInsertMultipleUserMutation,
    useUpdateUserMutation,
    useUpdateMultipleUserMutation,
    useDeleteUserMutation,
    useDeleteMultipleUserMutation,
    useGetUserUserEmailQuery,
    
} = userApi
